import { GAME } from '@/config/game';
import { MAPS } from '@/config/maps';
import type { ThemeDef } from '@/config/themes';
import type { Rng } from '@/core/rng';
import type { Arena } from './arena';
import { rectContains, type Rect } from './geometry';

/** Draw layers of the static decor, bottom to top. */
export const DecorLayer = { Decal: 0, Pool: 1, Rock: 2, Wall: 3, Prop: 4 } as const;
export type DecorLayer = (typeof DecorLayer)[keyof typeof DecorLayer];

/** One static sprite of a room (pure data — arena-view.ts instantiates it). */
export interface DecorItem {
  key: string;
  frame: number;
  x: number;
  y: number;
  scale: number;
  rotation: number;
  tint: number;
  layer: DecorLayer;
}

/** Variants per decor sheet (procedural/env-*.ts). */
const SHEET_FRAMES = { wall: 3, rock: 3, decal: 6, prop: 6, pool: 2 } as const;
/** Generated sprite sizes (px) — scale = wanted radius / half of these */
const SIZE = { pool: 192, pit: 192, rockRef: 70 } as const;
const WHITE = 0xffffff;

/**
 * Place every static decor sprite of a room: edge walls (doorways left open),
 * wall-dressing props IN the wall band, floor decals off obstacles/doors and
 * the obstacle visuals. Deterministic per decor rng (never the run rng).
 */
export function placeDecor(arena: Arena, theme: ThemeDef, rng: Rng): DecorItem[] {
  const out: DecorItem[] = [];
  for (const piece of arena.pieces) {
    edges(piece.outer, arena, theme, rng, out);
    decals(piece.inner, arena, theme, rng, out);
  }
  obstacles(arena, theme, rng, out);
  out.sort((a, b) => a.layer - b.layer);
  return out;
}

/** Is (x, y) inside any doorway (+ margin)? Walls/props skip those spans. */
function inDoor(arena: Arena, x: number, y: number, margin: number): boolean {
  for (const d of arena.doors) if (rectContains(d.rect, x, y, margin)) return true;
  return false;
}

function edges(o: Rect, arena: Arena, theme: ThemeDef, rng: Rng, out: DecorItem[]): void {
  const t = GAME.arena.wallThickness;
  const D = MAPS.decor;
  const step = D.wallSpacing;
  const inset = t * 0.2;
  const propGap = 1000 / D.propsPerKpx;
  let propNext = rng.range(0, propGap);
  let run = 0;
  const place = (x: number, y: number, nx: number, ny: number): void => {
    if (inDoor(arena, x, y, step * 0.4)) return;
    out.push({
      key: theme.wall, frame: rng.int(0, SHEET_FRAMES.wall - 1),
      x: x + rng.range(-8, 8), y: y + rng.range(-8, 8),
      scale: rng.range(0.85, 1.15), rotation: 0, tint: WHITE, layer: DecorLayer.Wall,
    });
    run += step;
    if (run < propNext) return;
    run = 0;
    propNext = rng.range(propGap * 0.5, propGap * 1.5);
    // Props lean against the wall: pushed into the band, facing the room
    const pin = t * D.propInset;
    out.push({
      key: theme.props, frame: rng.int(0, SHEET_FRAMES.prop - 1),
      x: x + nx * pin, y: y + ny * pin,
      scale: rng.range(D.propScale[0], D.propScale[1]),
      rotation: Math.atan2(ny, nx) + Math.PI / 2 + rng.range(-0.25, 0.25),
      tint: WHITE, layer: DecorLayer.Prop,
    });
  };
  for (let x = o.x; x <= o.x + o.w; x += step) {
    place(x, o.y + inset, 0, 1);
    place(x, o.y + o.h - inset, 0, -1);
  }
  for (let y = o.y + step; y < o.y + o.h; y += step) {
    place(o.x + inset, y, 1, 0);
    place(o.x + o.w - inset, y, -1, 0);
  }
}

function decals(inner: Rect, arena: Arena, theme: ThemeDef, rng: Rng, out: DecorItem[]): void {
  const D = MAPS.decor;
  const count = Math.round((inner.w * inner.h / 1e6) * D.decalsPerMpx);
  for (let i = 0; i < count; i++) {
    const x = rng.range(inner.x, inner.x + inner.w);
    const y = rng.range(inner.y, inner.y + inner.h);
    const frame = rng.int(0, SHEET_FRAMES.decal - 1);
    const scale = rng.range(D.decalScale[0], D.decalScale[1]);
    const rotation = rng.range(0, Math.PI * 2);
    if (inDoor(arena, x, y, D.decalClear) || !clearOfObstacles(arena, x, y, D.decalClear)) continue;
    out.push({ key: theme.decals, frame, x, y, scale, rotation, tint: WHITE, layer: DecorLayer.Decal });
  }
}

function clearOfObstacles(arena: Arena, x: number, y: number, pad: number): boolean {
  for (const ob of arena.obstacles) {
    const r = ob.radius + pad;
    if ((x - ob.x) ** 2 + (y - ob.y) ** 2 < r * r) return false;
  }
  return true;
}

function obstacles(arena: Arena, theme: ThemeDef, rng: Rng, out: DecorItem[]): void {
  for (const ob of arena.obstacles) {
    if (ob.lava) {
      out.push({
        key: theme.hazard, frame: rng.int(0, SHEET_FRAMES.pool - 1), x: ob.x, y: ob.y,
        scale: (ob.radius * 2.2) / SIZE.pool, rotation: rng.range(0, Math.PI * 2),
        tint: WHITE, layer: DecorLayer.Pool,
      });
    } else if (ob.pit) {
      out.push({
        key: 'env/pit', frame: 0, x: ob.x, y: ob.y, scale: (ob.radius * 2.15) / SIZE.pit,
        rotation: rng.range(0, Math.PI * 2), tint: theme.pitTint, layer: DecorLayer.Pool,
      });
    } else if (ob.landmark) {
      out.push({
        key: theme.landmark, frame: 0, x: ob.x, y: ob.y,
        scale: (ob.radius * 2.3) / MAPS.decor.landmarkSize, rotation: 0, tint: WHITE, layer: DecorLayer.Rock,
      });
    } else {
      // Rock cluster: 4-7 pieces filling the radius
      const count = Math.max(4, Math.round(ob.radius / 22));
      for (let i = 0; i < count; i++) {
        const a = (Math.PI * 2 * i) / count + rng.range(-0.3, 0.3);
        const dist = rng.range(0, ob.radius * 0.55);
        out.push({
          key: theme.rock, frame: rng.int(0, SHEET_FRAMES.rock - 1),
          x: ob.x + Math.cos(a) * dist, y: ob.y + Math.sin(a) * dist,
          scale: rng.range(0.8, 1.3) * (ob.radius / SIZE.rockRef), rotation: rng.range(-0.4, 0.4),
          tint: WHITE, layer: DecorLayer.Rock,
        });
      }
    }
  }
}
